import { HttpInterceptorFn, HttpResponse } from '@angular/common/http';
import { of, tap } from 'rxjs';

/**
 * In-memory HTTP GET cache.
 *
 * Caches successful GET responses for the duration of the browser session.
 * This prevents duplicate API calls when the user navigates back to a page
 * that already loaded its data (e.g. revisiting /products or /about-us).
 *
 * TTL is set per-request via the X-Cache-TTL header (seconds).
 * Default TTL: 5 minutes. Set to 0 to bypass the cache.
 *
 * Usage (in a service):
 *   this.http.get('/api/products', {
 *     headers: { 'X-Cache-TTL': '300' }
 *   });
 */

interface CacheEntry {
  response: HttpResponse<unknown>;
  expiresAt: number;
}

const cache = new Map<string, CacheEntry>();
const DEFAULT_TTL_MS = 5 * 60 * 1000; // 5 minutes

export const httpCacheInterceptor: HttpInterceptorFn = (req, next) => {
  // Only cache GET requests
  if (req.method !== 'GET') {
    return next(req);
  }

  // Respect X-Cache-TTL: 0 means bypass
  const ttlHeader = req.headers.get('X-Cache-TTL');
  const ttlMs = ttlHeader !== null
    ? parseInt(ttlHeader, 10) * 1000
    : DEFAULT_TTL_MS;

  if (ttlMs === 0) {
    return next(req);
  }

  // Strip the custom header before forwarding to the server
  const cleanReq = req.clone({ headers: req.headers.delete('X-Cache-TTL') });
  const cacheKey = cleanReq.urlWithParams;

  const cached = cache.get(cacheKey);
  if (cached && Date.now() < cached.expiresAt) {
    return of(cached.response.clone());
  }

  return next(cleanReq).pipe(
    tap(event => {
      if (event instanceof HttpResponse && event.status === 200) {
        cache.set(cacheKey, {
          response: event.clone(),
          expiresAt: Date.now() + ttlMs,
        });
      }
    }),
  );
};
